import { useEffect } from 'react';

import { cx } from '@emotion/css';
import { Col } from 'antd';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { doClearSubmitAccount, doSubmitAccount } from 'src/actions';
import { AccountApplicationCategoryNameDto } from 'src/dtos';
import { useAccountApplicationSelector, useAccountSelector } from 'src/hooks';
import { MButton } from 'src/lib';
import { AccountApplicationCategoryView } from 'src/models';
import { BaseStyle, Heading, Spacing } from 'src/styles';

import { AccountApplicationCategoryInformation } from './AccountApplicationCategoryInformation';
import { getEntityAccountApplicationValidationSchema } from './AccountApplicationForm.validation';

export type AccountApplicationFormValues = {
  [K in (typeof AccountApplicationCategoryNameDto)[keyof typeof AccountApplicationCategoryNameDto]]?: string;
};

export const AccountApplicationForm = () => {
  const loadingAccountSubmit: boolean = useSelector((state: any) => state.accounts.submit.isLoading);
  const categoryViewList: AccountApplicationCategoryView[] = useSelector(
    (state: any) => state.app.accountApplicationCategoryViewList,
  );

  const dispatch = useDispatch();
  const { account } = useAccountSelector();
  const { accountApplication, hasAccountApplicationSucceeded } = useAccountApplicationSelector();

  const isApplicationSubmitted = () => Boolean(account?.accountId);

  const findInitialValues = () => {
    let result: AccountApplicationFormValues = {};

    if (accountApplication?.categories) {
      result = accountApplication.categories.reduce((all, current) => {
        return { ...all, [current.name.value]: current.uploadedDocuments?.[0]?.id };
      }, {});
    }

    return result;
  };

  useEffect(() => {
    return () => {
      dispatch(doClearSubmitAccount());
    };
  }, []);

  return (
    <Formik<AccountApplicationFormValues>
      validateOnChange
      enableReinitialize
      initialValues={findInitialValues()}
      validationSchema={getEntityAccountApplicationValidationSchema(account?.entityAccountType)}
      onSubmit={() => {
        dispatch(
          doSubmitAccount({
            params: {
              id: account?.id,
            },
          }),
        );
      }}>
      {form => {
        useEffect(() => {
          //TODO: save in app reducer if account application is already submitted because form.submitCount is reset to 0
          if (hasAccountApplicationSucceeded && form.submitCount > 0) {
            accountApplication?.categories.forEach(category => {
              form.setFieldValue(category.name.value, category.uploadedDocuments?.[0]?.id, true);
            });
          }
        }, [hasAccountApplicationSucceeded, form.submitCount]);

        useEffect(() => {
          if (form.submitCount > 0) {
            //TODO: dispatch app action to save the flag that account application is submitted
          }

          return () => {
            //TODO: dispatch action to reset the flag when component unmounts
          };
        }, [form.submitCount]);

        return (
          <>
            {categoryViewList.map(category => (
              <AccountApplicationCategoryInformation
                key={category.key}
                category={category}
                entityAccountType={account?.entityAccountType}
                form={form}
                testId={category.key.toLocaleLowerCase()}
              />
            ))}
            {!isApplicationSubmitted() && (
              <Col span={24} className={cx(BaseStyle.level, Spacing.my12)}>
                <p className={Heading.subtitle} data-testid={'non-submitted-account-disclaimer'}>
                  Once you have Uploaded all the required documents above, please click &quot;Create Account&quot;
                </p>
              </Col>
            )}
            {!isApplicationSubmitted() && (
              <Col span={24} className={cx(BaseStyle.level, Spacing.my12)}>
                <MButton
                  loading={loadingAccountSubmit}
                  disabled={!form.isValid || loadingAccountSubmit}
                  onClick={() => {
                    form.submitForm();
                  }}
                  testId={`create-entity-account-button`}>
                  Create Account
                </MButton>
              </Col>
            )}
          </>
        );
      }}
    </Formik>
  );
};
